import { Duration } from 'luxon'

import type { TimeSlotForResident } from '../../../common/validations/reservationCalendarSchemas'
import type { CalculatedPrivateSlot } from './types'

/**
 * @param timeString  should come straight from backend so is in format "HH:mm:ss" ("HH:mm" works too).
 */
export const formatTimeForCalendar = (timeString: string): string =>
  timeString.substring(0, 5)

export const getSlotSizeMinutes = (duration?: string): number => {
  if (!duration) {
    return 60
  }

  const parsed = Duration.fromISO(duration)
  if (!parsed.isValid) {
    return 60
  }

  const totalMinutes = parsed.shiftTo('minutes').minutes

  return totalMinutes > 0 ? totalMinutes : 60
}

export const formatPrice = (price: string | null): string | null => {
  return price ? parseFloat(price).toFixed(2).replace('.', ',') : null
}

const toSingleSlotEntry = (
  slot: TimeSlotForResident
): CalculatedPrivateSlot => ({
  id: slot.id,
  startsAt: slot.startsAt,
  endsAt: slot.endsAt,
  status: slot.status,
  reservations: [...slot.reservations],
  timeSlots: [slot],
})

const groupPrivateSlots = (
  sorted: TimeSlotForResident[],
  groupSize: number
): CalculatedPrivateSlot[] => {
  const result: CalculatedPrivateSlot[] = []

  for (let i = 0; i < sorted.length; i++) {
    const slot = sorted[i]

    if (slot.isPrivateSlotStart) {
      const group = sorted.slice(i, i + groupSize)
      const uniqueReservations = [
        ...new Map(
          group.flatMap((s) => s.reservations).map((r) => [r.id, r])
        ).values(),
      ]
      result.push({
        id: group[0].id,
        startsAt: group[0].startsAt,
        endsAt: group[group.length - 1].endsAt,
        status: group[0].status,
        reservations: uniqueReservations,
        timeSlots: group,
      })
      i += groupSize - 1
      continue
    }

    result.push(toSingleSlotEntry(slot))
  }

  return result
}

const assignDisplayStatus = (
  slots: CalculatedPrivateSlot[],
  groupSize: number
): CalculatedPrivateSlot[] =>
  slots.map((slot) => {
    if (slot.status !== 'free') return slot
    if (slot.timeSlots.length === groupSize) return slot
    return { ...slot, status: 'reserved' as const }
  })

const canMergeReservations = (
  last: CalculatedPrivateSlot,
  current: CalculatedPrivateSlot
): boolean => {
  if (last.status === 'free' || current.status === 'free') return false
  if (last.endsAt !== current.startsAt) return false
  if (last.status !== current.status) return false
  const lastIds = new Set(last.reservations.map((r) => r.id))
  const currentIds = new Set(current.reservations.map((r) => r.id))
  if (lastIds.size === 0 && currentIds.size === 0) return true
  if (lastIds.size !== currentIds.size) return false
  return [...lastIds].every((id) => currentIds.has(id))
}

const mergeConsecutiveReservations = (
  slots: CalculatedPrivateSlot[]
): CalculatedPrivateSlot[] => {
  if (slots.length === 0) return []

  const result: CalculatedPrivateSlot[] = [slots[0]]

  for (let i = 1; i < slots.length; i++) {
    const last = result[result.length - 1]
    const current = slots[i]

    if (canMergeReservations(last, current)) {
      last.endsAt = current.endsAt
      last.timeSlots.push(...current.timeSlots)
      for (const r of current.reservations) {
        if (!last.reservations.some((e) => e.id === r.id)) {
          last.reservations.push(r)
        }
      }
    } else {
      result.push(current)
    }
  }

  return result
}

export const groupTimeSlotsIntoPrivateSlots = (
  timeSlots: TimeSlotForResident[],
  groupSize: number
): CalculatedPrivateSlot[] => {
  const sorted = [...timeSlots].sort((a, b) =>
    a.startsAt.localeCompare(b.startsAt)
  )

  const grouped = groupPrivateSlots(sorted, groupSize)
  const withStatus = assignDisplayStatus(grouped, groupSize)
  return mergeConsecutiveReservations(withStatus)
}
